import type { ContentItem } from '../domain/models'

export const seedContentItems: readonly ContentItem[] = [
  {
    id: 'content-proceed-with-caution',
    type: 'update',
    title: 'Proceed With Caution: start here',
    summary: 'Open the music, then keep your show-night tools close.',
    body: 'Music links, tour stops, tickets, and city updates—all in one place.',
    publishedAt: '2026-08-10T23:00:00-04:00',
    detailLayout: 'article',
    sections: [
      {
        id: 'music-first',
        title: 'Start with the music',
        body: 'Pick where you want to listen.',
        items: [
          'Choose the music hub when you want to pick a service.',
          'Use Spotify when you want to open the artist profile directly.',
        ],
      },
      {
        id: 'show-tools',
        title: 'Set up your stop',
        body: 'Turn listening into a useful show-night route inside the app.',
        items: [
          'Choose the city you are following.',
          'Connect your ticket and save the show details.',
          'Check that city’s Updates before you leave.',
        ],
      },
    ],
    callout: {
      label: 'Keep it simple',
      body: 'Music and social links open the band’s pages and profiles.',
    },
    actions: [
      { label: 'Open official music links', description: 'Choose where to listen.', target: 'official-links' },
      { label: 'Choose a tour stop', description: 'Set the city you want to follow.', target: 'tour' },
    ],
  },
  {
    id: 'content-four-members',
    type: 'tour-note',
    title: 'Meet The Paradox',
    summary: 'Meet the Atlanta four-piece and keep up with the band.',
    body: 'The Paradox formed in Atlanta in summer 2024 and makes rock and pop-punk together.',
    publishedAt: '2026-08-10T22:50:00-04:00',
    detailLayout: 'article',
    sections: [
      {
        id: 'band-basics',
        title: 'The band',
        items: [
          'The Paradox formed in Atlanta in summer 2024.',
          'The artist’s About page names four band members.',
          'The band describes its direction through rock and pop-punk.',
        ],
      },
      {
        id: 'keep-up',
        title: 'Keep up with The Paradox',
        body: 'Visit the band’s website for music, tour news, and the shop. Use Tour Desk for show-night tools.',
      },
    ],
    callout: {
      label: 'Stay current',
      body: 'Dates and announcements can change. Check the band’s pages for the latest.',
    },
    actions: [
      { label: 'Visit the artist website', description: 'Read the band’s About and music pages.', target: 'artist-site' },
      { label: 'Open official links', description: 'Find music and social links.', target: 'official-links' },
    ],
  },
  {
    id: 'content-music-links',
    type: 'destination',
    title: 'Take The Paradox with you',
    summary: 'Listen, follow, join the message list, or shop through destinations linked by the artist.',
    body: 'Artist website, merch, Spotify, music hub, Laylo, Instagram, TikTok, and X.',
    publishedAt: '2026-08-10T22:40:00-04:00',
    detailLayout: 'official-links',
    sections: [
      {
        id: 'listen',
        title: 'Choose how to listen',
        items: [
          'Open Spotify for the artist profile.',
          'Use the music hub when you want to choose another listening service.',
        ],
      },
      {
        id: 'stay-close',
        title: 'Stay close',
        items: [
          'Use Laylo for the artist-linked message list.',
          'Open Instagram, TikTok, or X for the artist’s social profiles.',
          'Open the website for tour news and the merch store to shop.',
        ],
      },
    ],
    callout: {
      label: 'Before you leave the app',
      body: 'These links open external services. Each service controls its own account, privacy, accessibility, and purchase experience.',
    },
    actions: [],
  },
  {
    id: 'content-show-day-plan',
    type: 'guide',
    title: 'Build your show-night plan',
    summary: 'Work through the ticket, show, saved details, city updates, arrival plan, and check-in steps.',
    body: 'Your connected ticket, saved stop, itinerary, city updates, and check-in stay together here.',
    publishedAt: '2026-08-10T22:30:00-04:00',
    detailLayout: 'show-night-plan',
    sections: [
      {
        id: 'lock-basics',
        title: 'Lock the basics',
        items: [
          'Connect the ticket you plan to use.',
          'Confirm the city and show details.',
          'Save the show so it stays easy to find.',
        ],
      },
      {
        id: 'build-route',
        title: 'Give yourself a route',
        items: [
          'Review city Updates before you leave.',
          'Add arrival, meetup, and travel notes to My Plan.',
          'Check the app’s check-in status when show day arrives.',
        ],
      },
    ],
    callout: {
      label: 'Check-in status',
      body: 'If check-in is open, the app will show it for your selected stop.',
    },
    actions: [],
  },
  {
    id: 'content-show-day-essentials',
    type: 'guide',
    title: 'Show-Day Essentials',
    summary: 'Cover the practical basics before you leave without guessing at venue-specific rules.',
    body: 'Ticket access, charged phone, official venue information, arrival route, hydration, and hearing protection.',
    publishedAt: '2026-08-10T22:20:00-04:00',
    detailLayout: 'article',
    sections: [
      {
        id: 'before-leaving',
        title: 'Before you leave',
        items: [
          'Confirm where your ticket is stored and how you will open it.',
          'Charge your phone and bring the cable or battery you normally rely on.',
          'Review the official venue or ticket information for entry, bag, and accessibility details.',
        ],
      },
      {
        id: 'protect-night',
        title: 'Protect the night',
        items: [
          'Hydrate before and during the night while following venue guidance.',
          'Bring hearing protection if it supports your comfort.',
          'Choose a meetup point and a way home before the crowd gets loud.',
        ],
      },
      {
        id: 'arrival-plan',
        title: 'Keep arrival simple',
        body: 'Save the show, check your route and travel time, and add a realistic arrival note to My Plan.',
      },
    ],
    callout: {
      label: 'Before you head out',
      body: 'Venue policies can change. Check your venue or ticket provider on show day.',
    },
    actions: [
      { label: 'Open ticket wallet', description: 'Confirm the ticket you plan to use.', target: 'tickets' },
      { label: 'Open show details', description: 'Review the selected stop and save it.', target: 'selected-show' },
      { label: 'Open my plan', description: 'Add arrival, meetup, and travel notes.', target: 'itinerary' },
    ],
  },
  {
    id: 'content-city-community',
    type: 'tour-note',
    title: 'Find your city board',
    summary: 'Move between Main, Updates, Fits, Merch, and Scene without losing your city.',
    body: 'Every Community channel follows the city at the top of the page.',
    publishedAt: '2026-08-10T22:10:00-04:00',
    detailLayout: 'article',
    sections: [
      {
        id: 'pick-channel',
        title: 'Pick the right channel',
        items: [
          'Main: fan conversation for the selected city.',
          'Updates: show news for this stop.',
          'Fits: show-night looks and photos.',
          'Merch: current shop links and city posts.',
          'Scene: the crowd view for this stop.',
        ],
      },
      {
        id: 'keep-city-straight',
        title: 'Keep the city straight',
        body: 'Home and Community follow your active city. Change the selected show when you want another stop’s board, then check the city label before posting.',
      },
    ],
    callout: {
      label: 'Before you head out',
      body: 'Check your venue or ticket provider for entry times and policies.',
    },
    actions: [
      { label: 'Open city Main', description: 'Join the selected city’s fan conversation.', target: 'selected-community-main' },
      { label: 'Open city Updates', description: 'Review the selected city’s update channel.', target: 'selected-community-updates' },
    ],
  },
  {
    id: 'content-passport-mark',
    type: 'guide',
    title: 'How your passport mark works',
    summary: 'Connect a ticket, use check-in when it is available, and keep the completed stop in your passport.',
    body: 'A passport mark follows a completed ticket-backed check-in for the selected show.',
    publishedAt: '2026-08-10T22:00:00-04:00',
    detailLayout: 'article',
    sections: [
      {
        id: 'what-you-need',
        title: 'What you need',
        items: [
          'A connected ticket for the show.',
          'The correct show selected in the app.',
          'A check-in state that is open or already completed.',
        ],
      },
      {
        id: 'how-it-lands',
        title: 'How the mark lands',
        items: [
          'Open check-in status from the show or this guide.',
          'Complete the ticket-backed check-in when the stop allows it.',
          'Open Passport to see the saved tour mark.',
        ],
      },
    ],
    callout: {
      label: 'Check-in status',
      body: 'Use the check-in status shown for your selected stop.',
    },
    actions: [
      { label: 'Open check-in status', description: 'See check-in for the selected show.', target: 'selected-check-in' },
      { label: 'Open passport', description: 'See the marks already saved to your account.', target: 'passport' },
    ],
  },
] as const
